/* eslint-disable react/jsx-props-no-spreading */
import React, { useId } from 'react';
import { useFormContext } from 'react-hook-form';
import cx from 'classnames';
import { InputField } from '../InputField';
import { PasswordField } from '../PasswordField';
import { Label } from '../Label';
import { ErrorMsg } from '../ErrorMsg';
import { OtpInput } from '../OtpInput';
import styles from './styles.module.scss';

/**
 * @typedef {object} TextFieldProps
 * @property {string} name - The name of the field
 * @property {object} [form] - The form object containing React Hook Form methods
 * @property {string} [type] - The type of the input field (e.g., 'text', 'password', 'create-password', 'one-time-code')
 * @property {string} [placeholder] - The placeholder text for the input field
 */

/**
 * @typedef {{
 *   label: string;
 *   labelClassName?: string | undefined;
 * } | {
 *   ariaLabel: string;
 * }} LabelProps
 */

/**
 * TextField component renders a label, input or password field, and error message.
 *
 * @param {TextFieldProps & LabelProps} props - Component props
 * @returns {JSX.Element}
 */
const TextField = (props) => {
  const {
    name,
    ariaLabel,
    label,
    labelClassName,
    labelSize,
    type = 'text',
    ...fieldProps
  } = {
    label: undefined,
    labelClassName: undefined,
    labelSize: undefined,
    ariaLabel: undefined,
    ...props,
  };

  const {
    formState: { errors },
  } = useFormContext();

  const errorId = useId();

  /** @type {{ name: string; 'aria-invalid': 'true' | 'false'; 'aria-describedby': string | undefined; 'aria-label'?: string}} */
  const commonProps = {
    name,
    'aria-invalid': errors[name] ? 'true' : 'false',
    'aria-describedby': errors[name] ? errorId : undefined,
  };

  if (ariaLabel) {
    commonProps['aria-label'] = ariaLabel;
  }

  /**
   * Renders the appropriate input component based on the provided type
   */
  const renderInputComponent = () => {
    if (type.includes('password')) {
      return (
        <PasswordField
          createPassword={type === 'create-password'}
          {...fieldProps}
          {...commonProps}
        />
      );
    }

    if (type === 'one-time-code') {
      return <OtpInput pushPasswordManagerStrategy="none" {...fieldProps} {...commonProps} />;
    }

    return <InputField type={type} {...fieldProps} {...commonProps} />;
  };

  return (
    <>
      {!ariaLabel && (
        <Label
          name={name}
          label={label}
          className={labelClassName}
          labelSize={labelSize}
        />
      )}
      <div className={cx(styles.inputWrapper, { [styles.error]: errors[name] })}>
        {renderInputComponent()}
        {errors[name]?.message && (
          <ErrorMsg
            id={errorId}
            message={errors[name]?.message}
          />
        )}
      </div>
    </>
  );
};

export { TextField };
